import { createUseStyles } from 'react-jss';
import { withAlpha } from '@/styles/utils';
import { DESKTOP_FONT_SIZES, GLOBAL_LETTER_SPACING, GLOBAL_LINE_HEIGHT, MOBILE_FONT_SIZES, } from '@/styles/themes/components/consts';
export const MAX_LANDING_PAGE_WIDTH = 1160;
const TABLET_BREAKPOINT = 'md';
const LANDING_LAPTOP_BREAKPOINT = 'lg';
const GRADIENT_COLOR = '18, 17, 57';
const OPACITY_LOW = 0.12;
const OPACITY_HIGH = 0.2;
const STOP_LOW = 20;
const STOP_HIGH = 40;
const HERO_GRADIENT = `linear-gradient(to bottom, 
    transparent 0%, 
    rgba(${GRADIENT_COLOR}, ${OPACITY_LOW}) ${STOP_LOW}%, 
    rgba(${GRADIENT_COLOR}, ${OPACITY_HIGH}) ${STOP_HIGH}%, 
    rgba(${GRADIENT_COLOR}, ${OPACITY_HIGH}) ${100 - STOP_HIGH}%, 
    rgba(${GRADIENT_COLOR}, ${OPACITY_LOW}) ${100 - STOP_LOW}%, 
    transparent 100%)`;
export const useStyles = createUseStyles((theme) => ({
    root: {
        width: '100%',
    },
    hero: {
        backgroundColor: theme.colors.brand,
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        borderRadius: '0 0 24px 24px',
        overflow: 'hidden',
        [theme.breakpoints.up(LANDING_LAPTOP_BREAKPOINT)]: {
            borderRadius: 0,
        },
    },
    heroContainer: {
        position: 'relative',
        maxWidth: MAX_LANDING_PAGE_WIDTH,
        width: '100%',
        padding: '16px 0 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            paddingTop: 76,
            gap: 37,
        },
        [theme.breakpoints.up(LANDING_LAPTOP_BREAKPOINT)]: {
            padding: '44px 0px 40px',
            gap: 44,
        },
    },
    heroButtonContainer: {
        display: 'flex',
        marginTop: 0,
        width: '100%',
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            marginTop: 0,
            width: 'fit-content',
        },
    },
    titlesContainer: {
        isolation: 'isolate',
        zIndex: 2,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        [theme.breakpoints.up(LANDING_LAPTOP_BREAKPOINT)]: {
            marginBottom: 50,
        },
    },
    bgGradient: {
        position: 'absolute',
        background: HERO_GRADIENT,
        inset: '-109px -500px -100px',
        zIndex: -1,
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            display: 'none',
        },
    },
    heroTitle: {
        color: theme.colors.white,
        fontFamily: theme.font.secondary,
        fontSize: 24,
        fontWeight: 700,
        letterSpacing: '0.4px',
        textShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            fontSize: 28,
            letterSpacing: '0.7px',
        },
        '&[data-first]': {
            marginBottom: 2,
            [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
                marginBottom: -3,
            },
        },
        '&[data-first="false"]': {
            marginBottom: 9,
            [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
                marginBottom: 3,
            },
        },
    },
    subtitle: {
        color: theme.colors.white,
        fontSize: DESKTOP_FONT_SIZES.hintModalTitle,
        letterSpacing: GLOBAL_LETTER_SPACING,
        textShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
        marginTop: 8,
        [theme.breakpoints.up(LANDING_LAPTOP_BREAKPOINT)]: {
            marginTop: 20,
        },
    },
    caption: {
        color: withAlpha(theme.colors.white, 0.6),
        marginTop: 5,
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            marginTop: 14,
        },
    },
    heroAdvantages: {
        zIndex: 2,
        width: '100%',
        backgroundColor: 'rgb(15 17 56 / 22%)',
        borderRadius: 12,
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            width: 'fit-content',
            backgroundColor: 'rgb(15 17 56 / 37%)',
            borderRadius: 32,
        },
    },
    heroAdvantagesList: {
        display: 'flex',
        flexDirection: 'column',
        padding: 24,
        maxWidth: 900,
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            flexDirection: 'row',
            padding: '20px 30px 20px 24px',
        },
        [theme.breakpoints.up(LANDING_LAPTOP_BREAKPOINT)]: {
            padding: '20px 60px 20px 24px',
        },
    },
    advantage: {
        '& svg': {
            display: 'none',
            flexShrink: 0,
            width: 44,
            height: 44,
            marginRight: 16,
            '& path': {
                fill: theme.colors.brand,
            },
            '& rect': {
                fill: '#F5F7FA',
            },
            [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
                display: 'block',
            },
        },
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            display: 'flex',
        },
        '&:not(:last-child)::after': {
            content: '""',
            display: 'block',
            height: 2,
            width: '100%',
            backgroundColor: '#CCD6E499',
            margin: '16px 0',
            [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
                height: '100%',
                width: 2,
                margin: '0 23px',
            },
        },
    },
    advantageTitles: {
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        [theme.breakpoints.up(LANDING_LAPTOP_BREAKPOINT)]: {
            minWidth: 169,
        },
    },
    advantageTitle: {
        color: theme.colors.white,
        fontSize: 18,
        fontWeight: 500,
        letterSpacing: GLOBAL_LETTER_SPACING,
    },
    advantageDescription: {
        color: theme.colors.white,
        fontSize: DESKTOP_FONT_SIZES.main,
        letterSpacing: GLOBAL_LETTER_SPACING,
        '& br': {
            display: 'none',
            [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
                display: 'block',
            },
        },
    },
    imageContainer: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            display: 'block',
        },
    },
    mainImage: {
        minWidth: 439,
        userSelect: 'none',
        WebkitUserDrag: 'none',
        width: 'calc(73vw + 186px)',
        transform: 'translate(14px, -80px)',
        aspectRatio: '2436 / 2458',
        marginBottom: -228,
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            transform: 'none',
            position: 'absolute',
            top: -66,
            right: -58,
            width: 519,
        },
        [theme.breakpoints.up(LANDING_LAPTOP_BREAKPOINT)]: {
            transform: 'none',
            top: -87,
            right: -71,
            width: 629,
        },
    },
    main: {
        padding: '16px 0',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        backgroundColor: theme.colors.background,
        gap: 24,
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            gap: 40,
            padding: '40px 0',
        },
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    section: {
        maxWidth: MAX_LANDING_PAGE_WIDTH,
        width: '100%',
        padding: '0 16px',
        ['&[data-no-padding]']: {
            padding: 0,
            [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
                padding: '0 16px',
            },
        },
    },
    title: {
        color: theme.colors.sky,
        fontFamily: theme.font.secondary,
        fontSize: 22,
        letterSpacing: '0.25px',
        fontWeight: 700,
        marginBottom: 20,
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            fontSize: 28,
            letterSpacing: '0.4px',
        },
    },
    stepsContainer: {
        backgroundColor: theme.colors.cloud,
        padding: '20px 30px 20px 20px',
        borderRadius: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            backgroundColor: 'transparent',
            padding: 0,
        },
    },
    stepContainer: {
        backgroundColor: theme.colors.cloud,
        display: 'flex',
        gap: 16,
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            width: '100%',
            flexDirection: 'column',
            gap: 24,
            padding: 24,
            borderRadius: 32,
        },
    },
    stepsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            flexDirection: 'row',
            gap: 16,
        },
    },
    stepIndex: {
        flexShrink: 0,
        width: 44,
        height: 44,
        borderRadius: 12,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: theme.colors.brandTertiary,
        fontFamily: theme.font.secondary,
        fontSize: 20,
        fontWeight: 500,
        letterSpacing: '0.4px',
        color: theme.colors.brand,
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            backgroundColor: '#fff3f5',
            width: 56,
            height: 56,
            borderRadius: 16,
            fontSize: 30,
            fontWeight: 700,
        },
    },
    stepTitle: {
        color: theme.colors.sky,
        fontFamily: theme.font.secondary,
        fontSize: 16,
        fontWeight: 700,
        textWrap: 'balance',
        letterSpacing: '0.4px',
        marginBottom: 4,
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            marginBottom: 11,
        },
        '& .desktop-line-break': {
            display: 'none',
            [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
                display: 'block',
            },
        },
    },
    stepDescription: {
        color: theme.colors.textPrimary,
        fontSize: MOBILE_FONT_SIZES.main,
        fontWeight: 400,
        letterSpacing: GLOBAL_LETTER_SPACING,
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            fontSize: DESKTOP_FONT_SIZES.main,
        },
    },
    scrollButtonContainer: {
        padding: '0 16px',
        maxWidth: MAX_LANDING_PAGE_WIDTH,
        width: '100%',
    },
    infoList: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: 16,
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 24,
        },
    },
    infoContainer: {
        width: '100%',
        padding: 24,
        borderRadius: 24,
        backgroundColor: theme.colors.cloud,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            padding: 32,
            gap: 16,
            flexDirection: 'row-reverse',
        },
    },
    infoTitlesContainer: {
        flexGrow: 1,
    },
    infoTitle: {
        color: theme.colors.sky,
        fontFamily: theme.font.secondary,
        textWrap: 'balance',
        fontSize: 16,
        fontWeight: 700,
        letterSpacing: '0.25px',
        marginBottom: 6,
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            marginBottom: 9,
        },
    },
    infoDescription: {
        color: theme.colors.textPrimary,
        fontSize: MOBILE_FONT_SIZES.main,
        fontWeight: 400,
        letterSpacing: GLOBAL_LETTER_SPACING,
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            fontSize: DESKTOP_FONT_SIZES.main,
        },
        '& .mobile': {
            [theme.breakpoints.up(LANDING_LAPTOP_BREAKPOINT)]: {
                display: 'none',
            },
        },
        '& .desktop': {
            display: 'none',
            [theme.breakpoints.up(LANDING_LAPTOP_BREAKPOINT)]: {
                display: 'block',
            },
        },
    },
    infoImageContainer: {
        margin: '0 20px',
        display: 'flex',
        justifyContent: 'center',
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            margin: 0,
        },
    },
    infoImage: {
        pointerEvents: 'none',
        userSelect: 'none',
        maxWidth: 'min(178px, 100%)',
        flex: '0 0 auto',
        margin: '0 auto',
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            width: 148,
            height: 148,
            aspectRatio: '1 / 1',
        },
    },
    faqContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: 24,
        borderRadius: 24,
        backgroundColor: theme.colors.cloud,
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            padding: 32,
        },
    },
    faqTitle: {
        color: theme.colors.sky,
        fontFamily: theme.font.secondary,
        fontSize: 22,
        fontWeight: 700,
        letterSpacing: '0.25px',
    },
    faqItem: {
        '&:not(:last-child)': {
            borderBottom: `1px solid ${theme.colors.background}`,
        },
    },
    faqButton: {
        padding: '15px 4px 15px 0',
        display: 'flex',
        width: '100%',
        gap: 12,
        justifyContent: 'space-between',
        alignItems: 'center',
        fontWeight: 500,
        color: theme.colors.sky,
        textAlign: 'left',
        fontSize: MOBILE_FONT_SIZES.main,
        lineHeight: GLOBAL_LINE_HEIGHT,
        letterSpacing: GLOBAL_LETTER_SPACING,
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            fontSize: DESKTOP_FONT_SIZES.main,
        },
        '& svg': {
            flexShrink: 0,
            '& polyline': {
                stroke: theme.colors.textPrimary,
            },
        },
    },
    faqAnswer: {
        color: theme.colors.textPrimary,
        paddingBottom: 16,
        fontSize: MOBILE_FONT_SIZES.main,
        lineHeight: GLOBAL_LINE_HEIGHT,
        letterSpacing: GLOBAL_LETTER_SPACING,
        [theme.breakpoints.up(TABLET_BREAKPOINT)]: {
            fontSize: DESKTOP_FONT_SIZES.main,
        },
        '& a': {
            color: theme.colors.brand,
            textDecoration: 'none',
        },
    },
}), { name: 'landing' });
